import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { usePagination } from "../../../../hooks/usePagination";
import { useTenant } from "../../../../hooks/useTenant";
import { toast } from "react-toastify";
import { ConfirmDialog } from "../../../../components/Common";
import FinancialCategoryService from "../../../../services/financial_category_service";

/**
 * useFinancialCategories
 * Hook de lógica para o módulo de Categorias Financeiras (Plano de Contas / DRE).
 * Utiliza o FinancialCategoryService para persistência.
 * 
 * Auto-seed: Quando o tenant não possui categorias, cria as categorias padrão automaticamente.
 */
export const useFinancialCategories = () => {
    const { idTenant, user } = useTenant();
    const [isSaving, setIsSaving] = useState(false);
    const hasSeedRun = useRef(false);

    const paginationOptions = useMemo(() => ({
        pageSize: 100,
        orderField: "order",
        orderDirection: "asc"
    }), []);

    // Paginação das Categorias
    const { data: categories, loading, error, refresh } = usePagination(
        `tenants/${idTenant}/financialCategories`,
        idTenant,
        paginationOptions
    );

    // Auto-seed: cria categorias padrão se o tenant ainda não tem nenhuma
    useEffect(() => {
        if (!loading && categories.length === 0 && idTenant && user && !hasSeedRun.current) {
            hasSeedRun.current = true;
            (async () => {
                try {
                    const count = await FinancialCategoryService.seedDefaults(idTenant, user);
                    if (count > 0) {
                        toast.success(`${count} categorias padrão criadas!`);
                        refresh();
                    }
                } catch (err) {
                    console.error("[AutoSeed] Erro ao criar categorias padrão:", err);
                }
            })();
        }
    }, [loading, categories, idTenant, user, refresh]);

    /**
     * Salvar uma Categoria
     */
    const saveCategory = useCallback(async (formData) => {
        if (!idTenant) return;
        setIsSaving(true);

        try {
            await FinancialCategoryService.save(formData, idTenant, user, categories);
            toast.success(formData.id ? "Categoria atualizada!" : "Nova categoria cadastrada!");
            refresh();
            return true;
        } catch (err) {
            console.error("Erro ao salvar categoria:", err);
            toast.error("Erro ao salvar categoria.");
            return false;
        } finally {
            setIsSaving(false);
        }
    }, [idTenant, user, categories, refresh]);

    /**
     * Excluir uma Categoria
     */
    const deleteCategory = useCallback(async (categoryId) => {
        if (!idTenant) return;

        const categoryToDelete = categories.find(c => c.id === categoryId);

        // Bloqueia exclusão de categorias de sistema
        if (categoryToDelete?.isSystem) {
            toast.warning("Categorias padrão do sistema não podem ser excluídas.");
            return false;
        }

        const confirmed = await ConfirmDialog({
            title: "Excluir Categoria?",
            text: `Tem certeza que deseja excluir "${categoryToDelete?.name || 'esta categoria'}"? Esta ação não pode ser desfeita.`,
            confirmButtonText: "Sim, excluir",
            cancelButtonText: "Cancelar"
        });

        if (!confirmed) return;

        try {
            await FinancialCategoryService.delete(categoryId, idTenant, user, categoryToDelete?.name);
            toast.success("Categoria removida!");
            refresh();
            return true;
        } catch (err) {
            console.error("Erro ao excluir categoria:", err);
            toast.error("Não foi possível excluir.");
            return false;
        }
    }, [idTenant, user, categories, refresh]);

    /**
     * Seed de categorias padrão
     */
    const seedDefaults = useCallback(async () => {
        if (!idTenant) return;
        setIsSaving(true);

        try {
            const count = await FinancialCategoryService.seedDefaults(idTenant, user);
            if (count > 0) {
                toast.success(`${count} categorias padrão criadas!`);
                refresh();
            } else {
                toast.info("As categorias padrão já existem.");
            }
            return true;
        } catch (err) {
            console.error("Erro ao criar categorias padrão:", err);
            toast.error("Erro ao criar categorias padrão.");
            return false;
        } finally {
            setIsSaving(false);
        }
    }, [idTenant, user, refresh]);

    return {
        categories,
        loading,
        error,
        isSaving,
        actions: {
            saveCategory,
            deleteCategory,
            seedDefaults,
            refresh
        }
    };
};
